<template>
  <el-dialog
    :title="typeof contact.id !== 'undefined' ? 'Edit Contact' : 'Create Contact'"
    visible
    top="5vh"
    :fullscreen="isFullscreen"
    :close-on-click-modal="false"
    @close="$emit('update:isShow', false)"
  >
    <el-form
      ref="contactForm"
      v-loading="isSaving"
      :rules="rules"
      :model="contact"
      size="small"
    >
      <el-row :gutter="10">
        <el-col :md="8">
          <el-form-item
            label="Title"
            prop="title"
          >
            <el-input
              v-model="contact.title"
              placeholder="Title"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Name"
            prop="name"
          >
            <el-input
              v-model="contact.name"
              placeholder="Name"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Contact Type"
            prop="contact_type"
          >
            <el-select
              v-model="contact.contact_type"
              :loading="isLoadingContactTypes"
              value-key="id"
              placeholder="Select a contact type"
              class="w-100"
            >
              <el-option
                v-for="contactType in contactTypes"
                :key="contactType.id"
                :label="contactType.long || '-'"
                :value="contactType"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col>
          <el-form-item
            label="Address"
            prop="address"
          >
            <el-input
              v-model="contact.address"
              placeholder="Address of contact"
              clearable
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="8">
          <el-form-item
            label="Phone"
            prop="phone"
          >
            <el-input
              v-model="contact.phone"
              placeholder="Phone"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Mobile"
            prop="mobile"
          >
            <el-input
              v-model="contact.mobile"
              placeholder="Mobile"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Email"
            prop="email"
            :rules="[{ type: 'email', message: 'Please input correct email address', trigger: ['blur', 'change'] }]"
          >
            <el-input
              v-model="contact.email"
              placeholder="Email"
              clearable
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :md="12">
          <el-form-item
            label="Included in Billing"
            prop="include_billing"
          >
            <el-select
              v-model="contact.include_billing"
              value-key="id"
              placeholder="Included in Billing"
              class="w-100"
              default-first-option
              clearable
            >
              <el-option
                label="No"
                :value="false"
              />
              <el-option
                label="Yes"
                :value="true"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :md="12">
          <el-form-item
            label="Included in Pickups"
            prop="include_pickups"
          >
            <el-select
              v-model="contact.include_pickups"
              value-key="id"
              placeholder="Included in Pickups"
              class="w-100"
              default-first-option
              clearable
            >
              <el-option
                label="No"
                :value="false"
              />
              <el-option
                label="Yes"
                :value="true"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col>
          <el-form-item
            label="Notes"
            prop="notes"
          >
            <el-input
              v-model="contact.notes"
              placeholder="Notes"
              type="textarea"
              :rows="4"
              clearable
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="isFullscreen = !isFullscreen">
        <FontAwesomeIcon
          v-if="isFullscreen"
          :icon="['fal', 'compress']"
        />
        <FontAwesomeIcon
          v-else
          :icon="['fal', 'expand']"
        />
      </el-button>
      <el-button @click.native="$emit('update:isShow', false)">
        Cancel
      </el-button>
      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="typeof contact.id !== 'undefined' ? updateContact() : createContact()"
      >
        {{ typeof contact.id !== 'undefined' ? 'Save' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    contact: {
      type: Object,
      required: true
    },
    accountId: {
      type: String,
      required: true
    },
    isShow: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      isSaving: false,
      isFullscreen: false,
      contactTypes: [],
      isLoadingContactTypes: false,
      rules: {
        title: [
          { required: true, message: 'Title of the contact is required', trigger: 'blur' }
        ],
        name: [
          { required: true, message: 'Name of the contact is required', trigger: 'blur' }
        ],
        contact_type: [
          { required: true, message: 'Contact type of the contact is required', trigger: 'change' }
        ]
      }
    }
  },
  created () {
    this.loadLists()
  },
  methods: {
    createContact () {
      this.$refs.contactForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.contact.create(builder => {
            builder.context = { resource: 'account', id: this.accountId }
            builder.include = ['contact_type']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    updateContact () {
      this.$refs.contactForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.contact.update(builder => {
            builder.context = { resource: 'account', id: this.accountId }
            builder.include = ['contact_type']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    loadLists () {
      this.isLoadingContactTypes = true
      this.$sdk.dictionary_contacttype.list(builder => {
        builder.page = 1
        builder.limit = 1000
        builder.order = 'uuid:asc'
      })
        .then(res => { this.contactTypes = res })
        .finally(() => {
          this.isLoadingContactTypes = false
        })
    }
  }
}
</script>

<style>

</style>
